/**
 * **Nudge** — `mockups/studio/client-detail.html` §Open items and §Work orders.
 *
 * One button, two call sites, and one mail: `notifications_send` with the
 * `approval_reminder` template the lead added this session. `notifications_send` is
 * on `actionFields.ts`'s `HAND_WRITTEN` list — its `message` is a discriminated union
 * over every template — so this is the component that owns its form, exactly as that
 * list says.
 *
 * **It is built from a decision, not from an open item**, and that is the whole
 * design of it. `approval_reminder` needs a reference, a kind, an amount, the day it
 * was sent and the day it expires; `OpenDecisionSchema` carries every one of them and
 * `StudioOpenItemSchema` carries none. So a task waiting on the client gets no Nudge —
 * there is nothing to remind them *about* that this mail could name — and the four
 * decisions do. The mockup's own three nudgeable rows are three decisions.
 *
 * `consequence` is the one field nothing computes. The template's own description:
 * "Never a threat and never blank; the whole reason to send a second email is that
 * the first one did not say this." So it is written here, once per kind, in the
 * studio's voice, and it is page copy rather than data.
 */

import { useState } from 'react'

import { DECISION_KIND_WORDS, type DecisionKind, type OpenDecisionView } from '@fareast/shared'

import { messageOf } from '../../../api/problem.js'
import { reviewOf, useStudioAction } from '../../../api/studio.js'
import { NeedsReviewNotice } from '../../../components/studio/NeedsReviewNotice.js'
import { Button } from '../../../components/portal/Button.js'
import { ConfirmDialog } from '../../../components/portal/Overlay.js'
import { Notice } from '../../../components/portal/Status.js'
import { Prose, Small, Muted } from '../../../components/portal/Text.js'
import { useFormat } from '../../../format/useFormat.js'
import { absoluteUrl } from '../../../integrations/navigate.js'

/**
 * Where the client reads it and decides. `/app/requests` holds all four kinds.
 *
 * `absoluteUrl` is `integrations/navigate.ts`'s and is the one builder — this was the
 * third, and the only one that concatenated rather than resolving, so it was also the
 * one that would have doubled a slash the day the origin gained a path. The string
 * goes into a mail.
 */
export function decisionUrl(ref: string): string {
  return absoluteUrl(`/app/requests#${ref.toLowerCase()}`)
}

/**
 * What happens if they do not answer, one sentence per kind.
 *
 * Copy, not data — see the header. Each is a consequence the studio can actually
 * carry out, because a reminder that threatens something nobody will do is worse than
 * no reminder.
 */
const CONSEQUENCE: Readonly<Record<DecisionKind, string>> = {
  quote:
    'The quote holds for thirty days from the day it was sent; after that we re-price it ' +
    'against the current rate card and the start date moves with it.',
  extra:
    'Nothing is started and nothing is billed until you approve it, so this sits outside ' +
    'the month it was meant for until we hear back.',
  package_change:
    'A package change takes effect on a billing day, so every month it is not answered is ' +
    'another month on the current package.',
  content_plan:
    'The month’s pieces are scheduled from this plan, so the batch shifts by however long ' +
    'the sign-off takes.',
}

export type NudgeButtonProps = {
  readonly client: string
  readonly decision: OpenDecisionView
}

export function NudgeButton({ client, decision }: NudgeButtonProps) {
  const format = useFormat()
  const send = useStudioAction('notifications_send')
  const [open, setOpen] = useState(false)
  const [sent, setSent] = useState(false)

  const review = reviewOf(send.data)
  const amount = decision.amount === null ? null : format.money(decision.amount)

  const run = async () => {
    try {
      const answer = await send.mutateAsync({
        client,
        message: {
          template: 'approval_reminder',
          params: {
            ref: decision.ref,
            kind: decision.kind,
            title: decision.title,
            amount: decision.amount,
            sent_on: format.date(decision.proposed_at, 'long'),
            expires_on: decision.valid_until === null ? null : format.day(decision.valid_until),
            consequence: CONSEQUENCE[decision.kind],
            url: decisionUrl(decision.ref),
          },
        },
      })
      setOpen(false)
      if (reviewOf(answer) === null) setSent(true)
    } catch {
      /*
       * The refusal is already on `send.error` and is printed under the button. It is
       * swallowed here rather than rethrown because `mutateAsync` rejecting inside an
       * event handler is an unhandled rejection, and the dialog stays open so the
       * reader can try again.
       */
    }
  }

  return (
    <>
      <Button
        size="sm"
        variant="ochre"
        disabled={send.isPending}
        onClick={() => {
          setOpen(true)
        }}
      >
        {send.isPending ? 'Sending…' : 'Nudge'}
      </Button>

      {sent ? (
        <Small>
          <Muted>Reminder sent.</Muted>
        </Small>
      ) : null}
      {review === null ? null : <NeedsReviewNotice review={review} />}
      {send.isError ? (
        <Notice need role="alert">
          {messageOf(send.error)}
        </Notice>
      ) : null}

      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title="Remind them about this"
        confirmLabel={
          amount === null
            ? `Send the reminder about ${decision.ref}`
            : `Send the reminder — ${amount} on ${decision.ref}`
        }
        cancelLabel="Not yet"
        busy={send.isPending}
        onConfirm={() => {
          void run()
        }}
      >
        <Prose>
          Everybody on this account who has approval mail switched on gets one email about the{' '}
          {DECISION_KIND_WORDS[decision.kind]} <b>{decision.title}</b>
          {amount === null ? '' : `, which commits ${amount}`}. It says how long it has been waiting
          and what happens if it stays that way — {CONSEQUENCE[decision.kind]}
        </Prose>
      </ConfirmDialog>
    </>
  )
}
